import React, { MutableRefObject, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useNavigate } from 'react-router';

import { useDelayedBoolean, useIsMounted } from '@paragon/client-tools';

type ControlRef = MutableRefObject<{
  fireOnSubmit(): void;
}>;

interface Props {
  disabled?: boolean;
  controlRef?: ControlRef;
  error?: React.ReactNode | null;

  onSubmit: () => Promise<void>;
  onCancel?: () => void;
}

export function useEditCancelBarControlRef(): ControlRef {
  return useRef({ fireOnSubmit: () => {} });
}

export function EditCancelBar({ disabled, controlRef, error, onSubmit, onCancel }: Props) {
  const navigate = useNavigate();
  const isMounted = useIsMounted();

  const [dialogOpen, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const [isWorking, isWorkingLong, setIsWorking] = useDelayedBoolean(false);

  const defaultCancelAction = useCallback(() => void navigate(-1), [navigate]);
  const onSubmitClick = useCallback(async () => {
    setIsWorking(true);

    try {
      await onSubmit();
    } finally {
      if (isMounted()) setIsWorking(false);
    }
  }, [isMounted, onSubmit, setIsWorking]);

  if (controlRef) {
    controlRef.current = { fireOnSubmit: onSubmitClick };
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginLeft: 'auto', '& > *': { m: 1, mr: 0 } }}>
      {isWorking && isWorkingLong ? (
        <CircularProgress />
      ) : (
        <>
          {error}
          <Button
            sx={{ m: 1 }}
            variant="outlined"
            disabled={isWorking}
            onClick={disabled ? (onCancel ?? defaultCancelAction) : handleClickOpen}
          >
            Cancel
          </Button>
          <Button
            sx={{ m: 1 }}
            color="primary"
            variant="contained"
            disabled={disabled || isWorking}
            onClick={onSubmitClick}
          >
            Save
          </Button>
        </>
      )}
      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        aria-labelledby="confirm-cancel"
        aria-describedby="confirm-cancellation-of-changes"
      >
        <DialogTitle id="confirm-cancel">Confirm Cancellation</DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-cancellation-of-changes">
            Any unsaved changes will be lost. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleClose}>
            Go Back
          </Button>
          <Button variant="contained" onClick={onCancel ?? defaultCancelAction} autoFocus>
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
